/**
 * AudioService — Atmosphere wrapper.
 *
 * Web Audio API wrapper for synthesized SFX + a shared HTMLAudioElement
 * helper (playBundledClip) for real CC0 sample playback. Each public
 * play* method either:
 *   - lazily opens an AudioContext via ensureContext() and synthesizes
 *     the cue (playConfirmedChime, playQRSting QR 4/Failure path,
 *     playVillainSting → VillainStingLibrary fns that still synth), OR
 *   - constructs an HTMLAudioElement for a bundled MP3 (playDiceTumbleSfx,
 *     playTarotShuffleSfx, playQRSting QR 1-3 path).
 *
 * Lifecycle:
 *   - Singleton; the AudioContext is created lazily on first user gesture
 *     (autoplay-policy compliance).
 *   - Every play* method returns a Promise that resolves silently when
 *     the audio subsystem is unreachable (jsdom, mic-only environment,
 *     autoplay-policy block). Atmosphere never crashes the briefing room.
 */
type AudioContextCtor = typeof AudioContext;

interface AudioGlobals {
  AudioContext?: AudioContextCtor;
  webkitAudioContext?: AudioContextCtor;
}

function resolveAudioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const g = window as unknown as AudioGlobals;
  return g.AudioContext ?? g.webkitAudioContext ?? null;
}

/**
 * Shared real-audio-sample player used by playDiceTumbleSfx +
 * playTarotShuffleSfx (and future bundled CC0 SFX). Creates an
 * HTMLAudioElement with the given src, clamps volume to [0, 1], calls
 * play(), and catches the play() rejection (autoplay-policy violations
 * are common on first-page-load before any user gesture).
 *
 * Returns a Promise that resolves once play() has been dispatched —
 * NOT when the clip finishes. The visual atmosphere (DiceRollTumble /
 * FameRecognitionCinematic) drives onComplete from its own animation
 * timer, so the audio doesn't need to gate completion.
 */
export async function playBundledClip(src: string, volume: number | undefined): Promise<void> {
  // Guard for jsdom + headless environments where the Audio constructor
  // is absent. Mirrors the AudioContext nullish-check precedent.
  const AudioCtor = (globalThis as unknown as { Audio?: typeof Audio }).Audio;
  if (typeof AudioCtor !== 'function') {
    return;
  }
  const clamped = volume == null ? 0.5 : Math.max(0, Math.min(1, volume));
  let el: HTMLAudioElement;
  try {
    el = new AudioCtor(src);
  } catch {
    return; // construction failed — silent per AudioService precedent
  }
  el.volume = clamped;
  try {
    await el.play();
  } catch {
    // Autoplay-policy NotAllowedError, etc. — silent per AudioService precedent.
  }
}

export class AudioService {
  private context: AudioContext | null = null;
  private failedToInit = false;

  private ensureContext(): AudioContext | null {
    if (this.context !== null) {
      return this.context;
    }
    if (this.failedToInit) {
      return null;
    }
    const Ctor = resolveAudioContextCtor();
    if (Ctor === null) {
      this.failedToInit = true;
      return null;
    }
    try {
      this.context = new Ctor();
      return this.context;
    } catch {
      this.failedToInit = true;
      return null;
    }
  }

  /**
   * Atmosphere A2 — dice tumble SFX (real-audio sample, CC0).
   *
   * 2026-06-04 SECOND REWRITE: switched from Web Audio synthesis to a
   * bundled CC0 MP3 sample (`/audio/dice-roll.mp3` — OpenGameArt
   * "2 dice roll (29 throws)" by soundripper, dice-12.wav variant).
   * Prior synth (3 discrete clacks + lowpass) was still rejected by the
   * user; real recorded audio is the correct fix.
   *
   * Lifecycle:
   *   - Lazily creates an HTMLAudioElement per call (no AudioContext
   *     dependency for this method — sample decode is browser-native).
   *   - Returns a Promise that resolves immediately after play() is
   *     dispatched (fire-and-forget pattern; matches the existing
   *     DiceRollTumble.tsx consumer that doesn't await result).
   *   - Browser autoplay-policy rejection is caught silently — atmosphere
   *     never crashes the briefing room.
   *
   * @param opts.volume    Peak gain 0.0–1.0 (default 0.5; clamped)
   * @param opts.durationMs Unused for the real-audio path; preserved in
   *                        the interface for back-compat with synth-era
   *                        callers (e.g., DiceRollTumble.tsx passes this).
   *                        The clip's intrinsic duration is ~1.17s; if a
   *                        caller wants hard truncation they can call
   *                        pause() externally.
   */
  async playDiceTumbleSfx(opts?: { volume?: number; durationMs?: number }): Promise<void> {
    void opts?.durationMs; // intentionally unused — see jsdoc
    return playBundledClip('/audio/dice-roll.mp3', opts?.volume);
  }

  /**
   * Atmosphere A4t — tarot card shuffle SFX (real-audio sample, CC0).
   *
   * Bundled CC0 MP3 (`/audio/tarot-shuffle.mp3` — OpenGameArt
   * "54 Casino sound effects" by Kenney.nl, card-shuffle.ogg → MP3).
   * Used by the Skorpios tarot-shuffle cinematic surface (A4t deferred
   * backlog item, now unblocked).
   *
   * Same lifecycle + autoplay-policy handling as playDiceTumbleSfx.
   *
   * @param opts.volume    Peak gain 0.0–1.0 (default 0.5; clamped)
   */
  async playTarotShuffleSfx(opts?: { volume?: number }): Promise<void> {
    return playBundledClip('/audio/tarot-shuffle.mp3', opts?.volume);
  }

  /**
   * Atmosphere A2 Slice B — "Confirmed." chime.
   *
   * A brief two-note rising sine chime (~A4 → E5) for the voice-
   * confirmation acknowledgment. Designed to play AFTER the user speaks
   * the D100 result back so the GM has aural feedback that the value
   * landed in the state machine. NOT auto-fired by DiceRollTumble —
   * the voice-confirmation integration (separate workstream) will call
   * this once it detects the spoken numeric value.
   *
   * @param opts.volume   Peak gain 0.0–1.0 (default 0.6 — chime is brief
   *                      and shouldn't compete with narration)
   */
  async playConfirmedChime(opts?: { volume?: number }): Promise<void> {
    const ctx = this.ensureContext();
    if (ctx === null) {
      return;
    }

    const volume = opts?.volume ?? 0.6;
    const now = ctx.currentTime;
    const noteDuration = 0.12;
    const totalDuration = noteDuration * 2;

    // Two sine notes rising — A4 (440 Hz) then E5 (660 Hz).
    const note1 = ctx.createOscillator();
    note1.type = 'sine';
    note1.frequency.setValueAtTime(440, now);

    const note2 = ctx.createOscillator();
    note2.type = 'sine';
    note2.frequency.setValueAtTime(660, now + noteDuration);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(volume, now + 0.01);
    gain.gain.linearRampToValueAtTime(0, now + noteDuration - 0.01);
    gain.gain.setValueAtTime(0, now + noteDuration);
    gain.gain.linearRampToValueAtTime(volume, now + noteDuration + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + totalDuration);

    note1.connect(gain);
    note2.connect(gain);
    gain.connect(ctx.destination);

    note1.start(now);
    note1.stop(now + noteDuration);
    note2.start(now + noteDuration);
    note2.stop(now + totalDuration);

    await new Promise<void>((resolve) => {
      note2.onended = () => resolve();
    });
  }

  /**
   * Atmosphere A3 — QR cinematic sting.
   *
   * Quality-rating-keyed orchestral cue fired AFTER the dice tumble
   * settles. Five distinct timbres (all real CC0 audio after P4 ship
   * 2026-06-05):
   *   - QR 1 (Excellent):  Kenney casino chips-stack-3.ogg, 0.37s, peak
   *                        0.85 — sharp casino-thematic "you nailed it"
   *                        thump on the reveal beat.
   *   - QR 2 (Very Good):  Same clip, peak 0.65.
   *   - QR 3 (Good):       Same clip, peak 0.45.
   *   - QR 4 (Acceptable): Kenney casino card-place-1.ogg, 0.69s,
   *                        peak 0.55 — single card placement, neutral
   *                        "you got the card down" feel.
   *   - Failure (null):    Kenney casino card-shove-1.ogg, 0.77s,
   *                        peak 0.55 — sweeping cards aside, "the dealer
   *                        takes your bet" loss gesture.
   *
   * Voice mode adds a ~0.15 volume boost across all QR levels so the
   * cinematic carries over speaker output when the GM is narrating.
   *
   * @param qr      Quality rating 1-4 OR null (Failure)
   * @param mode    Trigger path; 'voice' boosts volume by 0.15
   * @param opts.volume     Peak gain 0.0–1.0; if omitted, derived from qr
   * @param opts.durationMs Reserved — real-audio clips play their full
   *                        intrinsic duration. Accepted for API back-
   *                        compat with callers that still pass it.
   */
  async playQRSting(
    qr: 1 | 2 | 3 | 4 | null,
    mode: 'selector' | 'voice' = 'selector',
    opts?: { volume?: number; durationMs?: number },
  ): Promise<void> {
    // 2026-06-04 SECOND REWRITE swapped QR 1-3 brass cluster (the horn
    // timbre offender) for /audio/qr-success.mp3. 2026-06-05 P4 SHIP
    // swaps the remaining QR 4 + Failure (null) synth thunk paths for
    // distinct CC0 clips — full audio-bundling consistency across the
    // QR rating axis. opts.durationMs is now unused (real audio plays
    // its intrinsic length); kept in the signature so existing callers
    // don't break.

    const peakByQr: Record<1 | 2 | 3 | 4 | 'null', number> = {
      1: 0.85,
      2: 0.65,
      3: 0.45,
      4: 0.55,
      null: 0.55,
    };
    const srcByQr: Record<1 | 2 | 3 | 4 | 'null', string> = {
      1: '/audio/qr-success.mp3',
      2: '/audio/qr-success.mp3',
      3: '/audio/qr-success.mp3',
      4: '/audio/qr-acceptable.mp3',
      null: '/audio/qr-failure.mp3',
    };
    const key: 1 | 2 | 3 | 4 | 'null' = qr === null ? 'null' : qr;
    const voiceBoost = mode === 'voice' ? 0.15 : 0;
    const volume = Math.min(1, (opts?.volume ?? peakByQr[key]) + voiceBoost);

    return playBundledClip(srcByQr[key], volume);
  }

  /**
   * Atmosphere A4 — villain entrance sting.
   *
   * Dispatches to the standalone synth function for the named villain
   * (see `services/audio/stings/VillainStingLibrary.ts`). All six
   * canonical villains are synthesized — no external assets shipped.
   *
   * Audio is fire-and-forget. Failures are silent — the cinematic
   * never crashes the runner.
   */
  async playVillainSting(
    villainId: import('./stings/VillainStingLibrary').VillainId,
    opts?: { volume?: number; durationMs?: number },
  ): Promise<void> {
    const ctx = this.ensureContext();
    if (ctx === null) {
      return;
    }
    const { playVillainSting } = await import('./stings/VillainStingLibrary');
    return playVillainSting(villainId, ctx, opts);
  }

  isAvailable(): boolean {
    return this.ensureContext() !== null;
  }
}

let singleton: AudioService | null = null;

export function getAudioService(): AudioService {
  if (singleton === null) {
    singleton = new AudioService();
  }
  return singleton;
}

export function resetAudioServiceForTests(): void {
  singleton = null;
}
